import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Badge, Accordion, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import EquipmentOptions from './EquipmentOptions';
import blockedEquipment from '../data/blockedEquipmentData';
import EnergyStatsResume from './EnergyStatsResume';
import { Trash2, FileText } from 'lucide-react';

const EquipmentList = ({ equipment, onEquipmentSelect, selectedEquipment, updateConsumption, consumption, selectedProduct, onQuoteClick }) => {
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    if (Array.isArray(selectedEquipment)) {
      setSelectedEquipments(selectedEquipment);
      calculateTotalConsumption(selectedEquipment);
    } else {
      setSelectedEquipments([]);
    }
  }, [selectedEquipment]);

  const handleEquipmentOptionClick = (category, option) => {
    // Create a unique ID for this equipment instance
    const instanceId = Date.now() + Math.random();
    
    // Create the equipment object with the selected option
    const newEquipment = {
      ...category,
      instanceId,
      quantity: 1, 
      usage: option.utilization || 1,
      selectedOption: { ...option, categoryId: category.id }
    };
    
    // Add to selected equipment list
    const newSelected = [...selectedEquipments, newEquipment];
    setSelectedEquipments(newSelected);
    calculateTotalConsumption(newSelected);
    
    // Select this equipment
    onEquipmentSelect(newSelected);
  };

  const updateEquipment = (instanceId, field, value) => {
    const updatedEquipments = selectedEquipments.map(equip => {
      if (equip.instanceId === instanceId) {
        return { ...equip, [field]: parseInt(value) };
      }
      return equip;
    });
    
    setSelectedEquipments(updatedEquipments);
    calculateTotalConsumption(updatedEquipments);
  };

  const updateEquipmentOption = (instanceId, option) => {
    const updatedEquipments = selectedEquipments.map(equip => {
      if (equip.instanceId === instanceId) {
        return { 
          ...equip, 
          selectedOption: { ...option, categoryId: equip.id },
          usage: option.utilization || equip.usage || 1
        };
      }
      return equip;
    });
    
    setSelectedEquipments(updatedEquipments);
    calculateTotalConsumption(updatedEquipments);
  };

  const calculateTotalConsumption = (equipments) => {
    if (!Array.isArray(equipments)) return;
    const total = equipments.reduce((sum, equip) => {
      const power = equip.selectedOption?.watt || equip.puissance || 0;
      const quantity = equip.quantity || 1;
      const usage = equip.usage || 1;
      return sum + (power * quantity * usage);
    }, 0);
    
    updateConsumption(total);
  };

  const removeEquipment = (instanceId) => {
    const updatedEquipments = selectedEquipments.filter(equip => equip.instanceId !== instanceId);
    setSelectedEquipments(updatedEquipments);
    calculateTotalConsumption(updatedEquipments);
    
    if (selectedEquipment && selectedEquipment.instanceId === instanceId) {
      onEquipmentSelect(null);
    }
  };

  const getEquipmentConsumption = (equip) => {
    const power = equip.selectedOption?.watt || equip.puissance || 0;
    const quantity = equip.quantity || 1;
    const usage = equip.usage || 1;
    return power * quantity * usage;
  };

  const isEquipmentBlocked = (productId, equipmentId, optionId = null) => {
    if (productId === null || productId === undefined) return false;
    const productRule = blockedEquipment.find(p => p.productId === productId);
    if (!productRule) return false;

    const equipRule = productRule.blockedEquipment.find(e => e.equipmentId === equipmentId);
    if (!equipRule) return false;

    if (equipRule.blockedOptions.includes('ALL')) return true;

    if (optionId !== null) return equipRule.blockedOptions.includes(optionId);
    return false;
  };

  return (
    <div className="my-5">
      <h3 className="text-center mb-4">Liste des Équipements</h3>
      
      <Row>
        <Col md={8}>
          <div className="selected-equipments-wrapper">
          <Accordion defaultActiveKey="0" className="mb-4">
            {equipment.map((category, index) => (
              <Accordion.Item key={category.id} eventKey={index.toString()}>
                <Accordion.Header onClick={() => setActiveCategory(category.id)}>
                  <div className="d-flex align-items-center">
                    <img 
                      src={category.image} 
                      alt={category.name}
                      style={{ width: '30px', height: '30px', marginRight: '10px', objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/30/3D5A73/FFFFFF?text=⚡';
                      }}
                    />
                    {category.name}
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    {category.options.map(option => {
                      const isBlocked = isEquipmentBlocked(selectedProduct?.id, category.id, option.id);

                      return (
                        <Col md={6} key={option.id} className="mb-3">
                          <Card
                            className="h-100 equipment-option-card"
                            onClick={() => !isBlocked && handleEquipmentOptionClick(category, option)}
                            style={{
                              opacity: isBlocked ? 0.5 : 1,
                              cursor: isBlocked ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <Card.Body className="d-flex align-items-center p-2">
                              <img
                                src={option.image}
                                alt={option.name}
                                style={{ width: '40px', height: '40px', objectFit: 'contain', marginRight: '10px' }}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/40/3D5A73/FFFFFF?text=⚡';
                                }}
                              />
                              <div className="flex-grow-1">
                                <div className="fw-bold small">{option.name}</div>
                                <Badge bg="secondary" className="small">{option.watt} W</Badge>
                                {option.utilization && (
                                  <Badge bg="info" className="ms-1 small">{option.utilization}h/jour</Badge>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
          <EnergyStatsResume 
            production={selectedProduct.puissance} 
            consumption={consumption} 
          />
          <div className="text-center mt-3 d-flex justify-content-center gap-3">
            <button
              className="quote-custom-reset-btn"
              onClick={() => {
                setSelectedEquipments([]);
                updateConsumption(0);
                onEquipmentSelect([]);
              }}
            >
              <Trash2 size={20} className="me-2" />
              Réinitialiser
            </button>

            <button
              className="quote-custom-btn"
              onClick={onQuoteClick}
            >
              <FileText size={20} className="me-2" />
              Demander un devis
            </button>
          </div>

          </div>
        </Col>
        
        <Col md={4}>
          <div className="sticky-top" style={{ top: '20px' }}>
            <h5 className="mb-3 mt-3 mt-md-0">Équipements Sélectionnés ({selectedEquipments.length})</h5>
            
            {selectedEquipments.length === 0 ? (
              <p className="text-muted">Aucun équipement sélectionné</p>
            ) : (
              <div className="selected-equipments">
                {selectedEquipments.map(equip => (
                  <div key={equip.instanceId} className="mb-3 p-3 border rounded equipment-item">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="mb-1">{equip.name}</h6>
                        <div className="small text-muted">
                          {equip.selectedOption?.name || 'Option non sélectionnée'}
                        </div>
                      </div>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => removeEquipment(equip.instanceId)}
                        className="remove-btn"
                      >
                        <i className="fas fa-times"></i>
                      </Button>
                    </div>
                    
                    <EquipmentOptions 
                      equipment={equip} 
                      updateEquipment={updateEquipment}
                      updateEquipmentOption={updateEquipmentOption}
                    />
                    
                    <div className="mt-2 small">
                      Consommation: {getEquipmentConsumption(equip).toLocaleString()} Wh/j
                    </div>
                  </div>
                ))}
                
                <div className="mt-4 p-3 bg-light rounded position-relative">
                  <div className="position-absolute top-0 end-0 m-2 d-flex gap-2">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Réinitialiser</Tooltip>}
                    >
                      <button
                        style={{
                          background: '#facc15',
                          color: '#000',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setSelectedEquipments([]);
                          updateConsumption(0);
                          onEquipmentSelect([]);
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Demander un devis</Tooltip>}
                    >
                      <button
                        style={{
                          background: '#0d6efd',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={onQuoteClick}
                      >
                        <FileText size={18} />
                      </button>
                    </OverlayTrigger>
                  </div>

                  <h6>Consommation Totale</h6>
                  <div className="fs-4 fw-bold text-danger">{consumption.toLocaleString()} Wh/j</div>
                  <div className="progress mt-2" style={{ height: '10px' }}>
                    <div 
                      className="progress-bar bg-danger" 
                      role="progressbar" 
                      style={{ width: `${(consumption / 30000) * 100}%` }}
                    ></div>
                  </div>
                  <div className="small text-muted mt-1">
                    {((consumption / 30000) * 100).toFixed(1)}% de votre conso.
                  </div>
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default EquipmentList;